import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useIntl } from "react-intl";
import ButtonComponent from "components/common/ButtonComponent";
import CheckboxComponent from "components/common/Checkbox";
import {
  Container,
  StepperContainer,
  ExistingLinksBox,
  StepContent,
  Title,
  CheckboxGroup,
  NavigationButtons,
  ProgressContainer,
  TableContainerStyled,
  DownloadLink,
  CheckboxInline,
  StyledBox,
  StepItem,
  buttonStyles,
  buttonStylesDisabled,
  inlineStyles,
  StyledTextField,
  inputStyles,
  inputFileStyles,
} from "./LinkedCategoriesStyles";
import { steps } from "utils/Constants";
import {
  selectLinkedCategories,
  selectLinkedCategoriesLoading,
  selectLinkedCategoriesUploadProgress,
  selectLinkedCategoriesUploadSuccess,
} from "store/linked-categories/LinkedCategoriesSelectors";
import { useSelector, useDispatch } from "react-redux";
import {
  addLinkedCategorySetsRequest,
  fetchAllLinkedCategories,
  resetUploadSuccess,
} from "store/linked-categories/LinkedCategoriesActions";
import { LinkedCategory } from "store/linked-categories/LinkedCategoriesTypes";
import { CircularProgress } from "@material-ui/core";

export const LinkedCategoriesContainer = () => {
  const intl = useIntl();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [fileName, setFileName] = useState("");
  const [linkedFileName, setLinkedFileName] = useState("");
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [allStoresSelected, setAllStoresSelected] = useState(false);
  const [storesFromFile, setStoresFromFile] = useState<string[]>([]);
  const [selectedCatalogs, setSelectedCatalogs] = useState<string[]>([]);
  const [allCatalogsSelected, setAllCatalogsSelected] = useState(true);
  const [catalogsFromFile, setCatalogsFromFile] = useState<string[]>([]);
  const [catalogsUnderStores, setCatalogsUnderStores] = useState<
    Record<string, Set<string>>
  >({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedCSVContent, setGeneratedCSVContent] = useState<string>("");
  const [originalFileContent, setOriginalFileContent] = useState<string>("");
  const [fileDownloaded, setFileDownloaded] = useState(false);
  const [uploadedData, setUploadedData] = useState<any>(null);
  const existingLinks = useSelector(selectLinkedCategories);
  const isFetchingLinkedCategories = useSelector(selectLinkedCategoriesLoading);
  const uploadProgress = useSelector(selectLinkedCategoriesUploadProgress);
  const isUploadSuccess = useSelector(selectLinkedCategoriesUploadSuccess);
  // Utility function to iterate over existing links and categories
  const processExistingLinks = useCallback(
    (callback: (link: any, category: any) => void) => {
      existingLinks?.forEach((link) => {
        link.categories.forEach((category) => {
          callback(link, category);
        });
      });
    },
    [existingLinks],
  );

  // Utility function to parse CSV content
  const parseCSVContent = useCallback((content: string) => {
    const lines = content.split("\n").filter((line) => line.trim());
    if (lines.length < 3) return null;

    // Parse CSV lines properly, handling quoted values and commas
    const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }

      // Add the last field
      result.push(current.trim());
      return result;
    };

    const storeIds = parseCSVLine(lines[0]);
    const catalogIds = parseCSVLine(lines[1]);
    const categoryRows = lines.slice(2).map((line) => parseCSVLine(line));

    return {
      storeIds: storeIds.filter((id: string) => id),
      catalogIds: catalogIds.filter((id: string) => id),
      categoryRows: categoryRows.filter((row) =>
        row.some((cell) => cell && cell !== ""),
      ),
    };
  }, []);

  // Convert existingLinks to processedData format
  const processedData = useMemo(() => {
    if (!existingLinks || existingLinks.length === 0) {
      return null;
    }

    // Extract unique store IDs and catalog IDs from existing links
    const storeIds = new Set<string>();
    const catalogIds = new Set<string>();

    processExistingLinks((link, category) => {
      storeIds.add(category.storeId);
      catalogIds.add(category.catalogId);
    });

    // Convert to arrays and sort for consistency
    const storeIdsArray = Array.from(storeIds).sort();
    const catalogIdsArray = Array.from(catalogIds).sort();

    // Create a map to organize categories by store and catalog
    const categoryMap = new Map<string, Map<string, string>>();

    processExistingLinks((link, category) => {
      const key = `${category.storeId}-${category.catalogId}`;
      if (!categoryMap.has(key)) {
        categoryMap.set(key, new Map());
      }
      categoryMap.get(key)!.set(category.categoryId, category.categoryId);
    });

    // Create catalog IDs array that maps to each store column
    const catalogIdsForStores = storeIdsArray.map((storeId) => {
      // Find the catalog ID for this store
      for (const catalogId of catalogIdsArray) {
        const key = `${storeId}-${catalogId}`;
        if (categoryMap.has(key)) {
          return catalogId;
        }
      }
      return ""; // Fallback if no catalog found
    });

    // Build the categories matrix
    const categories: string[][] = [];

    // Group categories by linked category set ID
    const linkedCategorySetMap = new Map<
      string,
      Array<{ storeId: string; catalogId: string; categoryId: string }>
    >();

    processExistingLinks((link, category) => {
      const linkedCategorySetId = link.linkedCategorySetId;
      if (!linkedCategorySetMap.has(linkedCategorySetId)) {
        linkedCategorySetMap.set(linkedCategorySetId, []);
      }
      linkedCategorySetMap.get(linkedCategorySetId)!.push({
        storeId: category.storeId,
        catalogId: category.catalogId,
        categoryId: category.categoryId,
      });
    });

    // Create a row for each linked category set
    linkedCategorySetMap.forEach((categoryData, linkedCategorySetId) => {
      const row: string[] = [];

      // For each store, find the specific category that was linked for this store-catalog combination
      storeIdsArray.forEach((storeId, storeIndex) => {
        let categoryForThisStore = "";
        const catalogId = catalogIdsForStores[storeIndex];

        // Find the exact category for this store-catalog combination in this linked category set
        const categoryForStore = categoryData.find(
          (cat) => cat.storeId === storeId && cat.catalogId === catalogId,
        );

        if (categoryForStore) {
          categoryForThisStore = categoryForStore.categoryId;
        }

        row.push(categoryForThisStore);
      });

      categories.push(row);
    });

    return {
      storeIds: storeIdsArray,
      catalogIds: catalogIdsForStores,
      categories: categories,
    };
  }, [existingLinks, processExistingLinks]);

  // Table features state
  const [searchTerm, setSearchTerm] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset fileDownloaded when entering the download step
  useEffect(() => {
    if (activeStep === 3) {
      setFileDownloaded(false);
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep === 0) {
      dispatch(fetchAllLinkedCategories());
    }
  }, [activeStep, dispatch]);

  const linkedFileInputRef = useRef<HTMLInputElement>(null);
  const catalogHeaderRow: Record<string, any> = useMemo(() => {
    const row: Record<string, any> = { id: "catalogHeader" };
    row["rowIndex"] = intl.formatMessage({
      id: "linkedCategories.catalogs",
      defaultMessage: "Catalogs",
    }); // Add "Catalogs" to the first column
    processedData?.catalogIds?.forEach((catalog: string, index: number) => {
      row[`col${index}`] = catalog;
    });
    return row;
  }, [processedData?.catalogIds, intl]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "rowIndex",
        headerName: intl.formatMessage({
          id: "linkedCategories.stores",
          defaultMessage: "Stores",
        }),
        width: 80,
        sortable: false,
        valueGetter: (params) => params.row.rowIndex,
      },
      ...(processedData?.storeIds?.map((store, index) => ({
        field: `col${index}`,
        headerName: store,
        width: 150,
        sortable: false,
      })) || []),
    ],
    [processedData?.storeIds, intl],
  );

  const filteredRows = useMemo(() => {
    if (!processedData?.categories) return [];

    // Add catalog header row at the beginning
    const allRows = [catalogHeaderRow];

    // Add data rows
    const dataRows = processedData.categories
      .map((row, idx) => {
        const rowData: Record<string, any> = {
          id: idx,
          rowIndex: idx + 1,
        };
        row.forEach((cell, i) => {
          rowData[`col${i}`] = cell;
        });
        return rowData;
      })
      .filter((row) =>
        Object.values(row).some(
          (val) =>
            val &&
            val.toString().toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );

    return [...allRows, ...dataRows];
  }, [processedData?.categories, searchTerm, catalogHeaderRow]);

  const shouldUseScrollX = (processedData?.storeIds?.length ?? 0) > 4;

  // Handle pagination changes
  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      setPaginationModel(model);
    },
    [],
  );

  // Reset page to 0 when data changes, search term changes, or switching steps
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [processedData?.categories, searchTerm, activeStep]);

  // Create similar structures for uploaded data
  const uploadedCatalogHeaderRow: Record<string, any> = useMemo(() => {
    if (!uploadedData) return {};
    const row: Record<string, any> = { id: "uploadedCatalogHeader" };
    row["rowIndex"] = intl.formatMessage({
      id: "linkedCategories.catalogs",
      defaultMessage: "Catalogs",
    });
    uploadedData.catalogIds?.forEach((catalog: string, index: number) => {
      row[`col${index}`] = catalog;
    });
    return row;
  }, [uploadedData, intl]);

  const uploadedColumns: GridColDef[] = useMemo(() => {
    if (!uploadedData) return [];
    return [
      {
        field: "rowIndex",
        headerName: intl.formatMessage({
          id: "linkedCategories.stores",
          defaultMessage: "Stores",
        }),
        width: 80,
        sortable: false,
        valueGetter: (params) => params.row.rowIndex,
      },
      ...(uploadedData.storeIds?.map((store, index) => ({
        field: `col${index}`,
        headerName: store,
        width: 150,
        sortable: false,
      })) || []),
    ];
  }, [uploadedData, intl]);

  const uploadedRows = useMemo(() => {
    if (!uploadedData?.categoryRows) return [];

    // Add catalog header row at the beginning
    const allRows = [uploadedCatalogHeaderRow];

    // Add data rows
    const dataRows = uploadedData.categoryRows.map((row, idx) => {
      const rowData: Record<string, any> = {
        id: idx,
        rowIndex: idx + 1,
      };
      row.forEach((cell, i) => {
        rowData[`col${i}`] = cell;
      });
      return rowData;
    });

    return [...allRows, ...dataRows];
  }, [uploadedData, uploadedCatalogHeaderRow]);

  const shouldUseScrollXForUploaded = (uploadedData?.storeIds?.length ?? 0) > 4;

  // Helper function to parse CSV line and return both unique and non-unique arrays
  const parseCSVLine = (line: string) => {
    const items =
      line
        ?.split(",")
        .map((item) => item.trim())
        .filter((item) => item && item !== "") || [];

    return {
      unique: Array.from(new Set(items)),
      withDuplicates: items,
    };
  };

  // Function to parse CSV file and build store-catalog relationships
  const parseStoresAndCatalogsFromFile = useCallback(
    async (
      file: File,
    ): Promise<{
      stores: string[];
      catalogs: string[];
      catalogsUnderStores: Record<string, Set<string>>;
    }> => {
      const content = await file.text();
      const lines = content.split("\n");

      // Parse first line (stores)
      const { unique: stores, withDuplicates: firstLineStores } = parseCSVLine(
        lines[0],
      );

      // Parse second line (catalogs)
      const { unique: catalogs, withDuplicates: secondLineCatalogs } =
        parseCSVLine(lines[1]);

      // Build store-catalog relationships based on column position
      const catalogsUnderStores: Record<
        string,
        Set<string>
      > = firstLineStores?.reduce(
        (acc, store, index) => {
          if (secondLineCatalogs[index]) {
            if (!acc[store]) {
              acc[store] = new Set();
            }
            acc[store].add(secondLineCatalogs[index]);
          }
          return acc;
        },
        {} as Record<string, Set<string>>,
      ) || {};

      return { stores, catalogs, catalogsUnderStores };
    },
    [],
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const fullPath = event.target.value;
      setFileName(fullPath);

      // Read and store the file content
      const content = await file.text();
      setOriginalFileContent(content);

      const { stores, catalogs, catalogsUnderStores } =
        await parseStoresAndCatalogsFromFile(file);
      setStoresFromFile(stores);
      setCatalogsFromFile(catalogs);
      setCatalogsUnderStores(catalogsUnderStores);
      // Don't set selected catalogs here - they will be set based on selected stores
    },
    [parseStoresAndCatalogsFromFile],
  );

  const handleStoreSelection = (storeId: string) => {
    if (selectedStores.includes(storeId)) {
      const newSelectedStores = selectedStores.filter((id) => id !== storeId);
      setSelectedStores(newSelectedStores);
      if (allStoresSelected) {
        setAllStoresSelected(false);
      }
      // Update catalogs based on remaining selected stores
      updateCatalogsForSelectedStores(newSelectedStores);
    } else {
      const newSelectedStores = [...selectedStores, storeId];
      setSelectedStores(newSelectedStores);
      // Check if all stores are now selected
      if (
        newSelectedStores.length === storesFromFile.length &&
        storesFromFile.length > 0
      ) {
        setAllStoresSelected(true);
      }
      // Update catalogs based on new selected stores
      updateCatalogsForSelectedStores(newSelectedStores);
    }
  };

  const handleSelectAllStores = () => {
    if (allStoresSelected) {
      setSelectedStores([]);
      setAllStoresSelected(false);
      // Clear catalogs when no stores are selected
      setCatalogsFromFile([]);
      setSelectedCatalogs([]);
      setAllCatalogsSelected(false);
    } else {
      setSelectedStores(storesFromFile);
      setAllStoresSelected(true);
      // Show catalogs that belong to all stores from the data mapping
      const allCatalogsForStores = new Set<string>();
      storesFromFile.forEach((storeId) => {
        if (catalogsUnderStores[storeId]) {
          catalogsUnderStores[storeId].forEach((catalogId) => {
            allCatalogsForStores.add(catalogId);
          });
        }
      });
      const catalogsArray = Array.from(allCatalogsForStores);
      setCatalogsFromFile(catalogsArray);
      setSelectedCatalogs(catalogsArray);
      setAllCatalogsSelected(catalogsArray.length > 0);
    }
  };

  // Function to update catalogs based on selected stores
  const updateCatalogsForSelectedStores = (selectedStoresList: string[]) => {
    if (selectedStoresList.length === 0) {
      setCatalogsFromFile([]);
      setSelectedCatalogs([]);
      setAllCatalogsSelected(false);
    } else {
      // Get catalogs that belong to the selected stores from the data mapping
      const catalogsForSelectedStores = new Set<string>();
      selectedStoresList.forEach((storeId) => {
        if (catalogsUnderStores[storeId]) {
          catalogsUnderStores[storeId].forEach((catalogId) => {
            catalogsForSelectedStores.add(catalogId);
          });
        }
      });

      const catalogsArray = Array.from(catalogsForSelectedStores);
      setCatalogsFromFile(catalogsArray);
      setSelectedCatalogs(catalogsArray);
      // Update the "Select All" state based on whether all available catalogs are selected
      setAllCatalogsSelected(catalogsArray.length > 0);
    }
  };

  const handleCatalogSelection = (catalogId: string) => {
    if (selectedCatalogs.includes(catalogId)) {
      setSelectedCatalogs(selectedCatalogs.filter((id) => id !== catalogId));
      if (allCatalogsSelected) {
        setAllCatalogsSelected(false);
      }
    } else {
      const newSelectedCatalogs = [...selectedCatalogs, catalogId];
      setSelectedCatalogs(newSelectedCatalogs);
      // Check if all catalogs are now selected
      if (
        newSelectedCatalogs.length === catalogsFromFile.length &&
        catalogsFromFile.length > 0
      ) {
        setAllCatalogsSelected(true);
      }
    }
  };

  const handleSelectAllCatalogs = () => {
    if (allCatalogsSelected) {
      setSelectedCatalogs([]);
      setAllCatalogsSelected(false);
    } else {
      setSelectedCatalogs(catalogsFromFile);
      setAllCatalogsSelected(true);
    }
  };

  // Function to generate CSV for download based on selected stores and catalogs
  const generateDownloadCSV = useCallback(async () => {
    if (
      !fileName ||
      selectedStores.length === 0 ||
      selectedCatalogs.length === 0
    ) {
      console.warn("No stores or catalogs selected");
      return;
    }

    // Use the stored file content instead of reading from file input
    if (!originalFileContent) {
      console.warn("No file content available");
      return;
    }

    const lines = originalFileContent.split("\n");

    // Get the original store and catalog lines
    const originalStores =
      lines[0]?.split(",").map((item) => item.trim()) || [];
    const originalCatalogs =
      lines[1]?.split(",").map((item) => item.trim()) || [];

    // Create arrays for selected stores and catalogs in order
    const storeIds: string[] = [];
    const catalogIds: string[] = [];
    const categories: string[][] = [];

    // Build store and catalog arrays based on selection
    originalStores.forEach((store, index) => {
      // Only include store-catalog pairs that are actually selected
      if (
        selectedStores.includes(store) &&
        originalCatalogs[index] &&
        selectedCatalogs.includes(originalCatalogs[index])
      ) {
        storeIds.push(store);
        catalogIds.push(originalCatalogs[index]);
      }
    });

    // Process category data (lines 3 onwards)
    for (let i = 2; i < lines.length; i++) {
      if (lines[i]?.trim()) {
        const categoryRow = lines[i].split(",");
        const rowcat: string[] = [];
        let hasRelevantData = false;

        // Check if this row has data for selected stores AND selected catalogs
        for (let j = 0; j < categoryRow.length; j++) {
          const cellValue = categoryRow[j]?.trim();
          if (cellValue && cellValue !== "") {
            if (
              j < originalStores.length &&
              selectedStores.includes(originalStores[j]) &&
              originalCatalogs[j] &&
              selectedCatalogs.includes(originalCatalogs[j])
            ) {
              hasRelevantData = true;
              break;
            }
          }
        }

        // Only process rows with relevant data
        if (hasRelevantData) {
          // Build category row for selected stores AND selected catalogs
          for (let j = 0; j < originalStores.length; j++) {
            if (
              selectedStores.includes(originalStores[j]) &&
              originalCatalogs[j] &&
              selectedCatalogs.includes(originalCatalogs[j])
            ) {
              const cellValue = categoryRow[j]?.trim() || "";
              rowcat.push(cellValue);
            }
          }
          categories.push(rowcat);
        }
      }
    }

    // Build CSV content using the reference pattern
    const csvToDownloadText = [
      storeIds.join(","),
      catalogIds.join(","),
      ...categories.map((row) => row.join(",")),
    ].join("\r\n");

    // Store the generated CSV content
    setGeneratedCSVContent(csvToDownloadText);
  }, [fileName, selectedStores, selectedCatalogs, originalFileContent]);

  // Function to download the stored CSV content
  const downloadGeneratedCSV = useCallback(
    (e) => {
      e.preventDefault();
      if (!generatedCSVContent) {
        console.warn("No CSV content available for download");
        return;
      }
      const dataUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(generatedCSVContent)}`;
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "LinkedCategories.csv";
      link.click();
      setFileDownloaded(true);
    },
    [generatedCSVContent],
  );

  const handleNext = () => {
    if (activeStep === 2) {
      setIsProcessing(true);

      // Generate CSV and wait for it to complete
      const generateAndProceed = async () => {
        // Call the generate function and wait for it to complete
        await generateDownloadCSV();

        // Add a small delay to make the processing state visible
        await new Promise((resolve) => setTimeout(resolve, 500));

        setIsProcessing(false);
        setActiveStep((prevStep) => prevStep + 1);
      };

      generateAndProceed();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleGoToStep = (step: number) => {
    setActiveStep(step);
    handleResetData();
  };

  const handlePrevious = () => {
    setActiveStep((prevStep) => prevStep - 1);
    handleResetData();
  };

  const handleResetData = () => {
    setUploadedData(null);
    setLinkedFileName("");
    if (linkedFileInputRef.current) {
      linkedFileInputRef.current.value = "";
    }
    if (isUploadSuccess) {
      dispatch(resetUploadSuccess());
    }
  };

  const handleLinkedFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fullPath = event.target.value;
    setLinkedFileName(fullPath);
  };

  const handleUploadLinkedFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!linkedFileName) return;

    // Read the file content
    const fileInput = linkedFileInputRef.current;
    if (fileInput && fileInput.files && fileInput.files[0]) {
      const file = fileInput.files[0];
      const reader = new FileReader();

      reader.onload = (event) => {
        const fileContent = event.target?.result as string;

        // Parse the CSV content for display
        const parsedCSV = parseCSVContent(fileContent);
        if (parsedCSV) {
          setUploadedData(parsedCSV);
        }

        // Build the linked category sets request
        const payload = buildLinkedCategorySetsRequest(fileContent);

        if (payload) {
          dispatch(addLinkedCategorySetsRequest(payload));
        }
      };

      reader.readAsText(file);
    }
  };

  // Function to download existing links as CSV
  const downloadExistingLinksCSV = useCallback(() => {
    if (!processedData) return;

    // Generate CSV from existing links
    const csvContent = [
      processedData.storeIds.join(","),
      processedData.catalogIds.join(","),
      ...processedData.categories.map((row) => row.join(",")),
    ].join("\r\n");

    // Download the CSV
    const dataUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(csvContent)}`;
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = "ExistingLinkedCategories.csv";
    link.click();
  }, [processedData]);

  // Function to build linked category sets request from uploaded file
  const buildLinkedCategorySetsRequest = useCallback(
    (fileContent: string) => {
      const parsedCSV = parseCSVContent(fileContent);
      if (!parsedCSV) return null;

      const { storeIds, catalogIds, categoryRows } = parsedCSV;

      // Create a map of existing categories for quick lookup
      const existingCategoriesMap = new Map<string, string>(); // categoryId -> linkedCategorySetId
      processExistingLinks((link, category) => {
        // If this category exists anywhere, we'll use its linkedCategorySetId
        if (!existingCategoriesMap.has(category.categoryId)) {
          existingCategoriesMap.set(
            category.categoryId,
            link.linkedCategorySetId,
          );
        }
      });

      const linkedCategorySets: LinkedCategory[] = [];

      // Process each category row (starting from row 3) as a single linked category set
      for (let i = 0; i < categoryRows.length; i++) {
        const categoryRow = categoryRows[i];
        if (!Array.isArray(categoryRow)) continue;

        const categoryRowArray = categoryRow.map((cell: string) => cell.trim());

        // Get all non-empty category names from this row (including duplicates)
        const categoryNamesInRow = categoryRowArray.filter(
          (cell: string) => cell && cell !== "",
        );

        // Skip if no categories in this row
        if (categoryNamesInRow.length === 0) continue;

        // Find the first category name to check for existing linked category set
        const firstCategoryName = categoryNamesInRow[0];
        const existingLinkedCategorySetId =
          existingCategoriesMap.get(firstCategoryName);

        // Create categories array for all categories in this row
        const categories: Array<{
          catalogId: string;
          categoryId: string;
          storeId: string;
        }> = [];

        // For each store, add the category if it exists in that store
        storeIds.forEach((storeId: string, storeIndex: number) => {
          const cellValue = categoryRowArray[storeIndex];
          const catalogId = catalogIds[storeIndex];

          // Add if the cell has a category name
          if (cellValue && cellValue !== "") {
            categories.push({
              catalogId,
              categoryId: cellValue,
              storeId,
            });
          }
        });

        // Only add if we have categories in this row
        if (categories.length > 0) {
          linkedCategorySets.push({
            linkedCategorySetId: existingLinkedCategorySetId || "",
            categories,
          });
        }
      }

      return { linkedCategorySets };
    },
    [processExistingLinks, parseCSVContent],
  );

  // Reusable Navigation Buttons Component
  const NavigationButtonsComponent = ({
    canGoNext = false,
    isReadFile = false,
    isUploadFile = false,
  }: {
    canGoNext: boolean;
    isReadFile?: boolean;
    isUploadFile?: boolean;
  }) => (
    <NavigationButtons>
      {!isReadFile && (
        <ButtonComponent
          variant="contained"
          onClick={handlePrevious}
          style={buttonStyles(theme)}
        >
          {intl.formatMessage({
            id: "linkedCategories.previous",
            defaultMessage: "Prev",
          })}
        </ButtonComponent>
      )}
      {!isUploadFile ? (
        <ButtonComponent
          variant="contained"
          onClick={handleNext}
          disabled={!canGoNext}
          style={!canGoNext ? buttonStylesDisabled(theme) : buttonStyles(theme)}
        >
          {intl.formatMessage({
            id: "linkedCategories.next",
            defaultMessage: "Next",
          })}
        </ButtonComponent>
      ) : (
        <ButtonComponent
          variant="contained"
          onClick={() => handleGoToStep(0)}
          style={buttonStyles(theme)}
        >
          {intl.formatMessage({
            id: "linkedCategories.step1",
            defaultMessage: "Step 1",
          })}
        </ButtonComponent>
      )}
    </NavigationButtons>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <StepContent>
            <Title variant="h4">
              {intl.formatMessage({
                id: "linkedCategories.readFile",
                defaultMessage: "Read File",
              })}
            </Title>

            <Box textAlign="center">
              <StyledTextField
                variant="outlined"
                value={fileName}
                InputProps={{ readOnly: true }}
                sx={inputFileStyles(theme)}
              />
              <ButtonComponent
                variant="contained"
                onClick={() => fileInputRef.current?.click()}
                style={buttonStyles(theme)}
              >
                {intl.formatMessage({
                  id: "linkedCategories.open",
                  defaultMessage: "Open",
                })}
              </ButtonComponent>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={inlineStyles.fileInput}
                onChange={handleFileSelect}
              />
            </Box>
            <NavigationButtonsComponent
              canGoNext={!!fileName}
              isReadFile={true}
            />

            {isFetchingLinkedCategories && (
              <ProgressContainer>
                <CircularProgress />
                <Typography variant="h6" sx={inlineStyles.progressTypography}>
                  {intl.formatMessage({
                    id: "linkedCategories.checkingForExistingLinks",
                    defaultMessage: "Checking for existing links",
                  })}
                </Typography>
              </ProgressContainer>
            )}

            {!isFetchingLinkedCategories &&
              (existingLinks.length > 0 ? (
                <>
                  <ExistingLinksBox>
                    <Typography variant="linkedCategoriesText">
                      {intl.formatMessage({
                        id: "linkedCategories.accountHasExistingLinks",
                        defaultMessage:
                          "The account has existing links. To override these links, continue to use the tool.",
                      })}
                    </Typography>
                    <Typography variant="linkedCategoriesText">
                      {intl.formatMessage({
                        id: "linkedCategories.clickToDownload",
                        defaultMessage: "Click ",
                      })}
                      <DownloadLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          downloadExistingLinksCSV();
                        }}
                      >
                        {intl.formatMessage({
                          id: "linkedCategories.here",
                          defaultMessage: "here",
                        })}
                      </DownloadLink>
                      {intl.formatMessage({
                        id: "linkedCategories.downloadExistingLinks",
                        defaultMessage:
                          " to download the csv file ready for upload, containing existing links.",
                      })}
                    </Typography>
                    <ButtonComponent
                      variant="contained"
                      style={{
                        ...inlineStyles.uploadButton,
                        ...buttonStyles(theme),
                      }}
                      onClick={() => handleGoToStep(4)}
                    >
                      {intl.formatMessage({
                        id: "linkedCategories.goToUploadLinks",
                        defaultMessage: "Go to Upload Links",
                      })}
                    </ButtonComponent>
                    <Typography variant="h6">
                      {intl.formatMessage({
                        id: "linkedCategories.existingLinks",
                        defaultMessage: "Existing Links",
                      })}
                    </Typography>
                  </ExistingLinksBox>
                  <TableContainerStyled>
                    <Box sx={inlineStyles.tableBox}>
                      <Box sx={inlineStyles.searchContainer}>
                        <Typography
                          variant="title"
                          sx={inlineStyles.searchStyles}
                        >
                          {intl.formatMessage({
                            id: "linkedCategories.search",
                            defaultMessage: "Search",
                          })}
                          {":"}
                        </Typography>
                        <StyledTextField
                          size="small"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          sx={inputStyles(theme)}
                        />
                      </Box>
                      <Box
                        sx={{
                          ...inlineStyles.dataGridContainer,
                          overflowX: shouldUseScrollX ? "auto" : "hidden",
                        }}
                      >
                        <DataGrid
                          rows={filteredRows}
                          columns={columns}
                          pageSizeOptions={[5, 10, 25]}
                          paginationModel={paginationModel}
                          onPaginationModelChange={handlePaginationModelChange}
                          disableRowSelectionOnClick
                          getRowClassName={(params) =>
                            params.id === "catalogHeader" ? "header-row" : ""
                          }
                          autoHeight={true}
                          sx={{
                            ...inlineStyles.dataGridStyles(theme),
                          }}
                        />
                      </Box>
                    </Box>
                  </TableContainerStyled>
                </>
              ) : (
                <ExistingLinksBox>
                  <Typography variant="linkedCategoriesText">
                    {intl.formatMessage({
                      id: "linkedCategories.noExistingLinks",
                      defaultMessage:
                        "No existing links. Continue to use the tool.",
                    })}
                  </Typography>
                </ExistingLinksBox>
              ))}
          </StepContent>
        );

      case 1:
        return (
          <StepContent>
            <Title variant="h4">
              {intl.formatMessage({
                id: "linkedCategories.selectStore",
                defaultMessage: "Select Stores",
              })}
            </Title>
            <Box sx={inlineStyles.selectStoresCatalogsContainer}>
              <Box sx={inlineStyles.descriptionContainer}>
                <Typography variant="title">
                  {intl.formatMessage({
                    id: "linkedCategories.selectStoresDescription",
                    defaultMessage:
                      "Select the stores to include (Select at least one)",
                  })}
                </Typography>
                <CheckboxInline
                  control={
                    <CheckboxComponent
                      checkboxId="LinkedCategoriesSelectAll"
                      checked={allStoresSelected}
                      onChange={handleSelectAllStores}
                    />
                  }
                  label={intl.formatMessage({
                    id: "linkedCategories.selectAllStores",
                    defaultMessage: "Select all stores",
                  })}
                />
                <CheckboxGroup>
                  {storesFromFile.map((store) => (
                    <CheckboxInline
                      key={store}
                      control={
                        <CheckboxComponent
                          checkboxId="LinkedCategoriesStoreSelection"
                          checked={selectedStores.includes(store)}
                          onChange={() => handleStoreSelection(store)}
                        />
                      }
                      label={store}
                    />
                  ))}
                </CheckboxGroup>
              </Box>
              <NavigationButtonsComponent
                canGoNext={selectedStores.length > 0}
              />
            </Box>
          </StepContent>
        );
      case 2:
        return (
          <StepContent>
            <Title variant="h4">
              {intl.formatMessage({
                id: "linkedCategories.selectCatalog",
                defaultMessage: "Select Catalogs",
              })}
            </Title>
            <Box sx={inlineStyles.selectStoresCatalogsContainer}>
              <Box sx={inlineStyles.descriptionContainer}>
                <Typography variant="title">
                  {intl.formatMessage({
                    id: "linkedCategories.selectCatalogsDescription",
                    defaultMessage:
                      "Select the catalogs to include (If none selected all catalogs under selected stores are defaults)",
                  })}
                </Typography>
                <CheckboxInline
                  control={
                    <CheckboxComponent
                      checkboxId="LinkedCategoriesSelectAllCatalogs"
                      checked={allCatalogsSelected}
                      onChange={handleSelectAllCatalogs}
                    />
                  }
                  label={intl.formatMessage({
                    id: "linkedCategories.selectAllCatalogs",
                    defaultMessage: "Select all catalogs",
                  })}
                />
                <CheckboxGroup>
                  {catalogsFromFile.map((catalog) => (
                    <CheckboxInline
                      key={catalog}
                      control={
                        <CheckboxComponent
                          checkboxId="LinkedCategoriesCatalogSelection"
                          checked={selectedCatalogs.includes(catalog)}
                          onChange={() => handleCatalogSelection(catalog)}
                        />
                      }
                      label={catalog}
                    />
                  ))}
                </CheckboxGroup>
              </Box>
              {isProcessing && (
                <ProgressContainer>
                  <CircularProgress />
                  <Typography variant="h6" sx={inlineStyles.progressTypography}>
                    {intl.formatMessage({
                      id: "linkedCategories.processing",
                      defaultMessage: "Linking in progress...",
                    })}
                  </Typography>
                </ProgressContainer>
              )}
              <NavigationButtonsComponent
                canGoNext={selectedCatalogs.length > 0}
              />
            </Box>
          </StepContent>
        );
      case 3:
        return (
          <StepContent>
            <Title variant="h4">
              {intl.formatMessage({
                id: "linkedCategories.clickToDownload",
                defaultMessage: "Click ",
              })}
              <DownloadLink
                href="#"
                onClick={(e) => {
                  downloadGeneratedCSV(e);
                }}
              >
                {intl.formatMessage({
                  id: "linkedCategories.here",
                  defaultMessage: "here",
                })}
              </DownloadLink>{" "}
              {intl.formatMessage({
                id: "linkedCategories.toDownloadTheCsv",
                defaultMessage: "to download the csv",
              })}
            </Title>
            <NavigationButtonsComponent canGoNext={fileDownloaded} />
          </StepContent>
        );
      case 4:
        return (
          <StepContent>
            <Title variant="h4">
              {intl.formatMessage({
                id: "linkedCategories.uploadLinks",
                defaultMessage: "Upload Links",
              })}
            </Title>
            <Box textAlign="center">
              <StyledTextField
                variant="outlined"
                value={linkedFileName}
                InputProps={{ readOnly: true }}
                sx={inputFileStyles(theme)}
              />
              <ButtonComponent
                variant="contained"
                onClick={() => linkedFileInputRef.current?.click()}
                style={buttonStyles(theme)}
              >
                {intl.formatMessage({
                  id: "linkedCategories.open",
                  defaultMessage: "Open",
                })}
              </ButtonComponent>
              <input
                ref={linkedFileInputRef}
                type="file"
                accept=".csv"
                style={inlineStyles.fileInput}
                onChange={handleLinkedFileSelect}
              />
            </Box>
            <Box textAlign="center" padding={2}>
              <ButtonComponent
                variant="contained"
                disabled={!linkedFileName}
                onClick={handleUploadLinkedFile}
                style={
                  !linkedFileName
                    ? buttonStylesDisabled(theme)
                    : buttonStyles(theme)
                }
              >
                {intl.formatMessage({
                  id: "linkedCategories.uploadLinkedCategories",
                  defaultMessage: "Upload Linked Categories",
                })}
              </ButtonComponent>
            </Box>
            {uploadProgress && (
              <ProgressContainer>
                <Typography variant="h6">
                  {intl.formatMessage({
                    id: "linkedCategories.uploading",
                    defaultMessage: "Upload in progress...",
                  })}
                  <CircularProgress />
                </Typography>
              </ProgressContainer>
            )}
            {isUploadSuccess && uploadedData && (
              <>
                <Box textAlign="center">
                  <Typography variant="h6">
                    {intl.formatMessage({
                      id: "linkedCategories.categoriesLinked",
                      defaultMessage: "Categories linked",
                    })}
                  </Typography>
                </Box>

                <TableContainerStyled>
                  <Box sx={inlineStyles.linkedCategoriesTableBox}>
                    <Box
                      sx={{
                        ...inlineStyles.linkedCategoriesDataGridContainer,
                        overflowX: shouldUseScrollXForUploaded
                          ? "auto"
                          : "hidden",
                      }}
                    >
                      <DataGrid
                        rows={uploadedRows}
                        columns={uploadedColumns}
                        pageSizeOptions={[5, 10, 25]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        disableRowSelectionOnClick
                        getRowClassName={(params) =>
                          params.id === "uploadedCatalogHeader"
                            ? "header-row"
                            : ""
                        }
                        autoHeight={true}
                        sx={{
                          ...inlineStyles.dataGridStyles(theme),
                        }}
                      />
                    </Box>
                  </Box>
                </TableContainerStyled>
              </>
            )}
            <NavigationButtonsComponent
              canGoNext={!!linkedFileName}
              isReadFile={false}
              isUploadFile={true}
            />
          </StepContent>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Box>
        <StepperContainer>
          <StyledBox>
            {steps.map((label, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;

              return (
                <StepItem
                  key={label}
                  isActive={isActive}
                  isCompleted={isCompleted}
                >
                  {label}
                  {isCompleted ? (
                    <img
                      src="/images/check.png"
                      alt="Check Mark"
                      style={inlineStyles.icon}
                    />
                  ) : (
                    <img
                      src="/images/Right-Icon.png"
                      alt="Right Arrow"
                      style={inlineStyles.icon}
                    />
                  )}
                </StepItem>
              );
            })}
          </StyledBox>
        </StepperContainer>

        {renderStepContent(activeStep)}
      </Box>
    </Container>
  );
};

export default LinkedCategoriesContainer;
